// The timeline entry format shared by every capture hook:
//   { t, kind, level, title, detail, page }
// t is epoch milliseconds, so entries from different pages (and rrweb events,
// which also use epoch timestamps) line up on one clock.

/** @typedef {'debug' | 'info' | 'warn' | 'error'} Level */

/**
 * @typedef {object} TimelineEntry
 * @property {number} t Epoch milliseconds.
 * @property {string} kind See KIND_GROUP for the full list.
 * @property {Level} level
 * @property {string} title One line, human readable.
 * @property {Record<string, unknown>} detail Kind-specific data.
 * @property {string} [page] Id of the page (within the session) that produced the entry.
 */

/** Which ring buffer each kind lives in, so a noisy kind can't push out the others. */
export const KIND_GROUP = {
  console: 'console',
  error: 'errors',
  csp: 'errors',
  report: 'errors',
  network: 'network',
  beacon: 'network',
  websocket: 'network',
  sse: 'network',
  resource: 'resources',
  click: 'actions',
  input: 'actions',
  submit: 'actions',
  navigation: 'actions',
  page: 'actions',
  custom: 'actions',
};

export const GROUPS = ['console', 'errors', 'network', 'resources', 'actions'];

export function groupFor(kind) {
  return KIND_GROUP[kind] || 'actions';
}

/**
 * @param {string} kind
 * @param {Level} level
 * @param {string} title
 * @param {Record<string, unknown>} [detail]
 * @param {number} [t]
 * @returns {TimelineEntry}
 */
export function createEntry(kind, level, title, detail = {}, t = Date.now()) {
  return { t, kind, level, title: String(title), detail };
}

/** @param {TimelineEntry} entry */
export function isFailure(entry) {
  const d = entry.detail || {};
  if (d.noise) return false;
  if (entry.level === 'error') return true;
  if ((entry.kind === 'network' || entry.kind === 'resource') && typeof d.status === 'number') {
    if (d.status >= 400) return true;
  }
  if (d.graphqlErrors) return true;
  return false;
}

/** Short index of everything that went wrong, for ticket summaries and triage. */
export function deriveFailures(entries) {
  return entries.filter(isFailure).map((e) => ({
    t: e.t,
    kind: e.kind,
    level: e.level,
    title: e.title,
    page: e.page,
  }));
}

export function sortByTime(entries) {
  return entries.slice().sort((a, b) => a.t - b.t);
}

/** Counts shown in the panel and sent with the report. */
export function summarise(entries) {
  const summary = {
    console: { total: 0, errors: 0, warnings: 0 },
    network: { total: 0, failed: 0 },
    errors: 0,
    actions: 0,
    resources: 0,
  };
  for (const e of entries) {
    const group = groupFor(e.kind);
    if (group === 'console') {
      summary.console.total++;
      if (e.level === 'error') summary.console.errors++;
      if (e.level === 'warn') summary.console.warnings++;
    } else if (group === 'network') {
      if (e.kind === 'websocket' || e.kind === 'sse') continue;
      summary.network.total++;
      if (isFailure(e)) summary.network.failed++;
    } else if (group === 'errors') {
      summary.errors++;
    } else if (group === 'resources') {
      summary.resources++;
    } else {
      summary.actions++;
    }
  }
  return summary;
}
